import React, { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, Users, Network, Trophy, Coins, Award, Gift, Heart, Download, RefreshCw } from "lucide-react";
import { useTranslation, useFormat } from "../../i18n";
import { useAnalyticsFilters, useAnalyticsSection } from "../analytics/hooks";
import FilterBar from "../analytics/FilterBar";
import OverviewTab from "./analytics/OverviewTab";
import ParticipationTab from "./analytics/ParticipationTab";
import NetworkTab from "./analytics/NetworkTab";
import LeaderboardsTab from "./analytics/LeaderboardsTab";
import PointsTab from "./analytics/PointsTab";
import NominationsTab from "./analytics/NominationsTab";
import RewardsTab from "./analytics/RewardsTab";
import EngagementTab from "./analytics/EngagementTab";
import ExportTab from "./analytics/ExportTab";

const VIEWS = [
  { id: "overview", icon: LayoutDashboard },
  { id: "participation", icon: Users },
  { id: "network", icon: Network },
  { id: "leaderboards", icon: Trophy },
  { id: "points", icon: Coins },
  { id: "nominations", icon: Award },
  { id: "rewards", icon: Gift },
  { id: "engagement", icon: Heart },
  { id: "export", icon: Download },
];

/**
 * Admin analytics dashboard. One filter bar drives every view; the active view
 * lives in the URL (?tab=analytics&view=...) so links into a specific report
 * work, and filters persist per browser.
 */
const AnalyticsSection = () => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = VIEWS.some((v) => v.id === searchParams.get("view")) ? searchParams.get("view") : "overview";
  const { filters, setFilters, reset } = useAnalyticsFilters();
  // Options are fetched without the team/type filters so a stale saved filter
  // (a deleted team) cannot 404 the very call that would let us clear it.
  const optionFilters = useMemo(() => ({ preset: filters.preset, startDate: filters.startDate, endDate: filters.endDate, granularity: filters.granularity }), [filters.preset, filters.startDate, filters.endDate, filters.granularity]);
  const options = useAnalyticsSection("filters", optionFilters);

  const setView = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", "analytics");
      next.set("view", id);
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  // Section names inside insights map onto views.
  const navigate = useCallback((section) => setView(VIEWS.some((v) => v.id === section) ? section : "overview"), [setView]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["analytics"] });

  useEffect(() => {
    // A team or type that no longer exists must not silently filter everything out.
    if (!options.data) return;
    if (filters.teamId !== "all" && !options.data.teams.some((x) => x.id === filters.teamId)) setFilters({ teamId: "all" });
    if (filters.recognitionTypeId !== "all" && !options.data.recognition_types.some((x) => x.id === filters.recognitionTypeId)) setFilters({ recognitionTypeId: "all" });
  }, [options.data, filters.teamId, filters.recognitionTypeId, setFilters]);

  const scope = options.data?.scope;
  const scopeLine = scope
    ? `${fmt.date(new Date(`${scope.start_date}T00:00:00`), { month: "short", day: "numeric", year: "numeric" })} – ${fmt.date(new Date(`${scope.end_date}T00:00:00`), { month: "short", day: "numeric", year: "numeric" })} · ${t("scope_days", { count: scope.days })} · ${scope.timezone}`
    : "";

  const body = {
    overview: <OverviewTab filters={filters} options={options.data} onNavigate={navigate} />,
    participation: <ParticipationTab filters={filters} />,
    network: <NetworkTab filters={filters} />,
    leaderboards: <LeaderboardsTab filters={filters} />,
    points: <PointsTab filters={filters} />,
    nominations: <NominationsTab filters={filters} />,
    rewards: <RewardsTab filters={filters} />,
    engagement: <EngagementTab filters={filters} />,
    export: <ExportTab filters={filters} scope={scope} />,
  }[view];

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-slate-800">{t("title")}</h1>
          <p className="hidden sm:block text-sm text-slate-600 mt-1">{t("subtitle")}</p>
          {scopeLine && <p className="text-[11px] text-slate-400 mt-0.5">{scopeLine}</p>}
        </div>
        <button type="button" onClick={refresh} className="inline-flex items-center gap-1 sm:gap-2 p-2 sm:px-3 sm:py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm text-sm font-medium flex-shrink-0" title={t("refresh")}>
          <RefreshCw className={`w-4 h-4 ${options.isFetching ? "animate-spin" : ""}`} aria-hidden="true" />
          <span className="hidden sm:inline">{t("refresh")}</span>
        </button>
      </div>

      <FilterBar filters={filters} setFilters={setFilters} reset={reset} options={options.data} showGranularity={view !== "export" && view !== "leaderboards" && view !== "network"} showCompare={view !== "export"} />

      <nav className="bg-white rounded-2xl border border-slate-200 p-1" aria-label={t("views_label")}>
        <div className="flex gap-1 overflow-x-auto touch-scroll">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const active = view === v.id;
            return (
              <button key={v.id} type="button" onClick={() => setView(v.id)} aria-current={active ? "page" : undefined} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-purple-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}>
                <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span>{t(`views.${v.id}`)}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div key={view} className="tab-panel-in">{body}</div>
    </div>
  );
};

export default AnalyticsSection;
